import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Star, ExternalLink, Save, X } from "lucide-react";
import { toast } from "sonner";
import { ImageUploadField } from "@/components/ImageUploadField";

const PRICE_TIERS = ["$", "$$", "$$$"];
const SKILL_LEVELS = ["All", "Beginner", "Intermediate", "Advanced"];

const blankProduct = {
  title: "", brand: "", image: "", category_slug: "",
  affiliate_url: "", retailer: "",
  price_tier: "$$", skill_level: "All", coach_notes: "",
  pros: [], cons: [],
  is_top_pick: false, order_index: 100, published: true,
};

export default function AdminEquipment() {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState("");

  const [editing, setEditing] = useState(null); // product being edited (null = closed)
  const [showCatDialog, setShowCatDialog] = useState(false);

  const load = async () => {
    try {
      const [p, c] = await Promise.all([
        api.get("/admin/equipment/products"),
        api.get("/admin/equipment/categories"),
      ]);
      setProducts(p.data || []);
      setCategories(c.data || []);
    } catch (err) {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const removeProduct = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/admin/equipment/products/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  const filtered = filterCat ? products.filter((p) => p.category_slug === filterCat) : products;

  if (loading) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="admin-equipment-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="font-display uppercase text-4xl text-white">Equipment Recommendations</h1>
          <p className="text-zinc-400 text-sm mt-1">Curated gear with affiliate links. <Link to="/equipment" target="_blank" className="text-[#FF331F] hover:underline">View public page →</Link></p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#0F0F0F] border border-[#27272A] rounded-sm">
          <TabsTrigger value="products" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs">Products ({products.length})</TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs">Categories ({categories.length})</TabsTrigger>
        </TabsList>

        {/* Products tab */}
        <TabsContent value="products" className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <Select value={filterCat || "all"} onValueChange={(v) => setFilterCat(v === "all" ? "" : v)}>
              <SelectTrigger className="bg-[#0F0F0F] border-[#27272A] text-white w-64" data-testid="filter-category">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setEditing({ ...blankProduct, category_slug: filterCat || categories[0]?.slug || "" })} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="new-product-btn">
              <Plus className="h-4 w-4 mr-2" /> New product
            </Button>
          </div>

          {filtered.length === 0 ? (
            <div className="border border-[#27272A] bg-[#0F0F0F] p-10 text-center text-zinc-400">
              No products yet. Click <strong>New product</strong> to add your first pick.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((p) => {
                const cat = categories.find((c) => c.slug === p.category_slug);
                return (
                  <div key={p.id} className="border border-[#27272A] bg-[#0F0F0F] flex flex-col" data-testid={`product-card-${p.slug}`}>
                    <div className="relative aspect-square bg-[#050505] overflow-hidden">
                      {p.image && <img src={p.image} alt={p.title} className="w-full h-full object-cover" />}
                      {p.is_top_pick && (
                        <div className="absolute top-2 left-2 bg-[#FF331F] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 flex items-center gap-1">
                          <Star className="h-2.5 w-2.5 fill-white" /> Top pick
                        </div>
                      )}
                      {!p.published && (
                        <div className="absolute top-2 right-2 bg-zinc-800 text-amber-300 text-[10px] uppercase tracking-widest font-bold px-2 py-1">Draft</div>
                      )}
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1 flex items-center gap-2">
                        <span>{cat?.name || p.category_slug}</span>
                        {p.price_tier && <span className="text-[#FF331F]">{p.price_tier}</span>}
                      </div>
                      <h3 className="font-bold text-white text-sm leading-tight mb-1">{p.title}</h3>
                      {p.brand && <div className="text-xs text-zinc-400 mb-2">{p.brand}</div>}
                      {p.coach_notes && <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2 mb-3">{p.coach_notes}</p>}
                      <div className="mt-auto pt-3 border-t border-[#27272A] flex items-center justify-between gap-2">
                        <a href={p.affiliate_url} target="_blank" rel="noreferrer" className="text-xs text-zinc-400 hover:text-white inline-flex items-center">
                          <ExternalLink className="h-3 w-3 mr-1" /> Link
                        </a>
                        <div className="flex gap-2">
                          <button onClick={() => setEditing(p)} className="text-zinc-400 hover:text-[#FF331F]" data-testid={`edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                          <button onClick={() => removeProduct(p.id, p.title)} className="text-zinc-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Categories tab */}
        <TabsContent value="categories" className="mt-6">
          <div className="flex items-center justify-between mb-6">
            <p className="text-zinc-400 text-sm">9 default categories were auto-created. Add custom ones for jump ropes, apparel, apps, etc.</p>
            <Button onClick={() => setShowCatDialog({ name: "", description: "", order_index: 100 })} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="new-category-btn">
              <Plus className="h-4 w-4 mr-2" /> New category
            </Button>
          </div>
          <div className="border border-[#27272A] bg-[#0F0F0F] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] border-b border-[#27272A]">
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="p-4">Name</th><th className="p-4">Slug</th><th className="p-4">Products</th><th className="p-4">Order</th><th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id} className="border-b border-[#27272A] hover:bg-[#121212]">
                    <td className="p-4">
                      <div className="text-white font-bold">{c.name} {c.is_default && <span className="text-[9px] uppercase tracking-widest text-zinc-500 ml-1">(default)</span>}</div>
                      {c.description && <div className="text-xs text-zinc-500 mt-0.5 max-w-md">{c.description}</div>}
                    </td>
                    <td className="p-4 text-zinc-400 font-mono text-xs">/{c.slug}</td>
                    <td className="p-4 text-zinc-300">{c.product_count || 0}</td>
                    <td className="p-4 text-zinc-500">{c.order_index}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => setShowCatDialog(c)} className="text-zinc-400 hover:text-[#FF331F]"><Pencil className="h-4 w-4" /></button>
                        <button onClick={async () => {
                          if (!window.confirm(`Delete category "${c.name}"?`)) return;
                          try { await api.delete(`/admin/equipment/categories/${c.id}`); toast.success("Deleted"); load(); }
                          catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
                        }} className="text-zinc-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {editing && <ProductDialog product={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {showCatDialog && <CategoryDialog category={showCatDialog} onClose={() => setShowCatDialog(false)} onSaved={() => { setShowCatDialog(false); load(); }} />}
    </div>
  );
}

function ProductDialog({ product, categories, onClose, onSaved }) {
  const [form, setForm] = useState(product);
  const [saving, setSaving] = useState(false);
  const isNew = !product.id;

  const save = async () => {
    if (!form.title || !form.image || !form.category_slug || !form.affiliate_url) {
      toast.error("Title, image, category, and affiliate URL are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        pros: (form.pros || []).filter(Boolean),
        cons: (form.cons || []).filter(Boolean),
      };
      if (isNew) {
        await api.post("/admin/equipment/products", payload);
        toast.success("Product created");
      } else {
        await api.patch(`/admin/equipment/products/${product.id}`, payload);
        toast.success("Saved");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const updateList = (key, idx, value) => {
    const next = [...(form[key] || [])]; next[idx] = value;
    setForm({ ...form, [key]: next });
  };
  const addToList = (key) => setForm({ ...form, [key]: [...(form[key] || []), ""] });
  const removeFromList = (key, idx) => {
    const next = [...(form[key] || [])]; next.splice(idx, 1);
    setForm({ ...form, [key]: next });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase">{isNew ? "New product" : `Edit: ${product.title}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Title *"><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Hayabusa T3 16oz Boxing Gloves" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="product-title" /></Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Brand"><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Hayabusa" className="bg-[#050505] border-[#27272A] rounded-sm" /></Field>
            <Field label="Retailer"><Input value={form.retailer} onChange={(e) => setForm({ ...form, retailer: e.target.value })} placeholder="Amazon · Title · Ringside" className="bg-[#050505] border-[#27272A] rounded-sm" /></Field>
          </div>
          <Field label="Image *">
            <ImageUploadField value={form.image} onChange={(url) => setForm({ ...form, image: url })} label="" testid="product-image" />
          </Field>
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Category *">
              <Select value={form.category_slug} onValueChange={(v) => setForm({ ...form, category_slug: v })}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="product-category"><SelectValue placeholder="Pick" /></SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                  {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Price tier">
              <Select value={form.price_tier} onValueChange={(v) => setForm({ ...form, price_tier: v })}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                  {PRICE_TIERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Skill level">
              <Select value={form.skill_level} onValueChange={(v) => setForm({ ...form, skill_level: v })}>
                <SelectTrigger className="bg-[#050505] border-[#27272A] rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0F0F0F] border-[#27272A] text-white">
                  {SKILL_LEVELS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Affiliate URL *"><Input value={form.affiliate_url} onChange={(e) => setForm({ ...form, affiliate_url: e.target.value })} placeholder="https://amzn.to/..." className="bg-[#050505] border-[#27272A] rounded-sm font-mono text-xs" data-testid="product-url" /></Field>
          <Field label="Coach's notes (1–3 sentences why you recommend it)">
            <Textarea value={form.coach_notes} onChange={(e) => setForm({ ...form, coach_notes: e.target.value })} rows={3} placeholder="Best beginner-to-intermediate glove I've trained in. Solid wrist support, wide hitting surface, and the foam holds up after a year of bag work." className="bg-[#050505] border-[#27272A] rounded-sm" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <BulletList label="Pros" items={form.pros || []} onUpdate={(i, v) => updateList("pros", i, v)} onAdd={() => addToList("pros")} onRemove={(i) => removeFromList("pros", i)} accent="text-emerald-400" />
            <BulletList label="Cons" items={form.cons || []} onUpdate={(i, v) => updateList("cons", i, v)} onAdd={() => addToList("cons")} onRemove={(i) => removeFromList("cons", i)} accent="text-zinc-500" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4 items-end">
            <Field label="Order index (lower = first)"><Input type="number" value={form.order_index} onChange={(e) => setForm({ ...form, order_index: parseInt(e.target.value || "100", 10) })} className="bg-[#050505] border-[#27272A] rounded-sm" /></Field>
            <div className="flex items-center justify-between bg-[#050505] border border-[#27272A] rounded-sm px-3 py-2">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Coach&apos;s Top pick</Label>
              <Switch checked={!!form.is_top_pick} onCheckedChange={(v) => setForm({ ...form, is_top_pick: v })} className="data-[state=checked]:bg-[#FF331F]" data-testid="product-top-pick" />
            </div>
            <div className="flex items-center justify-between bg-[#050505] border border-[#27272A] rounded-sm px-3 py-2">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Published</Label>
              <Switch checked={!!form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} className="data-[state=checked]:bg-[#FF331F]" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-[#27272A] pt-4">
            <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="product-save">
              <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CategoryDialog({ category, onClose, onSaved }) {
  const [form, setForm] = useState(category);
  const [saving, setSaving] = useState(false);
  const isNew = !category.id;

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/equipment/categories", form);
      else await api.patch(`/admin/equipment/categories/${category.id}`, form);
      toast.success("Saved");
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-lg" data-testid="category-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">{isNew ? "New category" : "Edit category"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jump Ropes" className="bg-[#050505] border-[#27272A] rounded-sm" data-testid="category-name" /></Field>
          <Field label="Description"><Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#050505] border-[#27272A] rounded-sm" /></Field>
          <Field label="Order (lower = shown first)"><Input type="number" value={form.order_index || 100} onChange={(e) => setForm({ ...form, order_index: parseInt(e.target.value || "100", 10) })} className="bg-[#050505] border-[#27272A] rounded-sm" /></Field>
          <div className="flex items-center justify-end gap-2 border-t border-[#27272A] pt-4">
            <Button variant="ghost" onClick={onClose} className="text-zinc-400">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm" data-testid="category-save">
              <Save className="h-4 w-4 mr-2" /> Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</Label><div className="mt-1">{children}</div></div>;
}

function BulletList({ label, items, onUpdate, onAdd, onRemove, accent }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">{label}</Label>
      <div className="space-y-1.5 mt-1">
        {items.map((it, i) => (
          <div key={i} className="flex gap-1.5">
            <Input value={it} onChange={(e) => onUpdate(i, e.target.value)} placeholder="Add a point…" className="bg-[#050505] border-[#27272A] rounded-sm text-xs" />
            <button type="button" onClick={() => onRemove(i)} className="text-zinc-500 hover:text-red-500 px-2"><X className="h-3 w-3" /></button>
          </div>
        ))}
        <Button type="button" onClick={onAdd} variant="outline" size="sm" className={`border-[#27272A] bg-[#050505] hover:bg-[#1A1A1A] rounded-sm text-xs ${accent || ""}`}>
          <Plus className="h-3 w-3 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}
